import { useCallback, useMemo } from 'react';
import { FlightRoomSummary, FlightRoomUserStatus, FlightStatus, QueryKeys } from '@connect-x-app/shared-domain-models';
import { getFlightRoomStatus, sortFlightroomsByStd } from '@connect-x-app/shared-utils';
import { useQueryClient } from '@tanstack/react-query';

import { useChannelSessionStore, useFlightRoomSessionStore, useFlightRoomStore } from '../../stores';
import { useChatChatroomSocketSendEvents } from '../socketEvents';

export const useFlightRooms = () => {
  const queryClient = useQueryClient();
  const { setSelectedChannelId } = useChannelSessionStore();
  const {
    pendingNotificationType,
    selectedFlightRoomId,
    selectedFlightRoomTab,
    setPendingNotificationType,
    setSelectedFlightRoomId,
    setSelectedFlightRoomTab,
  } = useFlightRoomSessionStore();

  const {
    flightRooms: flightRoomsMap,
    removeFlightRoom: removeFlightRoomStore,
    resetFlightRooms: resetFlightRoomsStore,
    setFlightRooms: setFlightRoomsStore,
    updateFlightRoom: updateFlightRoomStore,
  } = useFlightRoomStore();

  const { sendChatJoinFlightRoom, sendChatLeaveFlightRoom } = useChatChatroomSocketSendEvents();

  const openFlightRoom = useCallback(
    (roomId: string) => {
      setSelectedFlightRoomId(roomId);
    },
    [setSelectedFlightRoomId],
  );

  const closeFlightRoom = useCallback(() => {
    setSelectedFlightRoomId(null);
    setSelectedChannelId(null);
  }, [setSelectedChannelId, setSelectedFlightRoomId]);

  const activeFlightRooms = useMemo(() => {
    return Array.from(flightRoomsMap.values())
      .filter(room => room.status === FlightStatus.ACTIVE)
      .sort(sortFlightroomsByStd);
  }, [flightRoomsMap]);

  const landedFlightRooms = useMemo(() => {
    return Array.from(flightRoomsMap.values())
      .filter(room => room.status === FlightStatus.LANDED)
      .sort(sortFlightroomsByStd);
  }, [flightRoomsMap]);

  const flightRoomsWithTask = useMemo(() => {
    return Array.from(flightRoomsMap.values())
      .filter(room => room.status === FlightStatus.TASKS && room.userStatus !== FlightRoomUserStatus.PENDING)
      .sort(sortFlightroomsByStd);
  }, [flightRoomsMap]);

  const selectedFlightRoom = useMemo(() => {
    if (selectedFlightRoomId) {
      const selectedRoom = flightRoomsMap.get(selectedFlightRoomId);
      return selectedRoom ?? null;
    }
    return null;
  }, [flightRoomsMap, selectedFlightRoomId]);

  const updateFlightRoom = useCallback(
    (room: FlightRoomSummary) => {
      updateFlightRoomStore({ ...room, status: getFlightRoomStatus(room) });
    },
    [updateFlightRoomStore],
  );

  const joinFlightRoom = useCallback(
    async (chatroomId: string) => {
      const ack = await sendChatJoinFlightRoom(chatroomId);
      if (ack?.ok) {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOMS] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOM, chatroomId] });
      }
      return ack;
    },
    [queryClient, sendChatJoinFlightRoom],
  );

  const leaveFlightRoom = useCallback(
    async (chatroomId: string) => {
      const response = await sendChatLeaveFlightRoom(chatroomId);
      if (response?.ok) {
        removeFlightRoomStore(chatroomId);
      }
      return response;
    },
    [sendChatLeaveFlightRoom, removeFlightRoomStore],
  );

  return {
    activeFlightRooms,
    closeFlightRoom,
    flightRoomsWithTask,
    joinFlightRoom,
    landedFlightRooms,
    leaveFlightRoom,
    openFlightRoom,
    pendingNotificationType,
    removeFlightRoom: removeFlightRoomStore,
    resetFlightRooms: resetFlightRoomsStore,
    selectedFlightRoom,
    selectedFlightRoomTab,
    setFlightRooms: setFlightRoomsStore,
    setPendingNotificationType,
    setSelectedFlightRoomTab,
    updateFlightRoom,
  };
};
